import { nowUtcIso } from '@/lib/dates'
import { newUuid } from '@/offline/queue'
import type { Device, DeviceImei, Purchase } from '@/offline/types'

export interface PurchaseFormInput {
  workspace_id: string
  category_id: string
  brand_code: string | null
  model: string
  storage?: string | null
  ram?: string | null
  condition?: string | null
  color?: string | null
  pta_status: 'approved' | 'non_pta' | 'unknown'
  carrier_lock_status?: 'unlocked' | 'carrier_locked' | 'unknown' | null
  mdm_status?: 'not_observed' | 'enrolled' | 'unknown' | null
  /** Raw IMEI strings (any separators); normalized to digits here. */
  imeis: string[]
  cost_paisa: number
  direct_cost_paisa: number
  purchased_at?: string
}

export interface BuiltPurchase {
  device: Device
  imeis: DeviceImei[]
  purchase: Purchase
  /** Payload sent to the apply_operation `purchase.create` handler. */
  payload: Record<string, unknown>
}

/** Strip everything but digits from an IMEI entry. */
export function normalizeImei(raw: string): string {
  return raw.replace(/\D/g, '')
}

/**
 * Build the local records AND the server payload for a purchase. All UUIDs are
 * generated client-side before the local save so local and remote ids match and the
 * outbox operation is idempotently retryable (plan section 7).
 */
export function buildPurchase(input: PurchaseFormInput): BuiltPurchase {
  const now = nowUtcIso()
  const deviceId = newUuid()
  const isApple = input.brand_code === 'apple'

  const imeis: DeviceImei[] = input.imeis
    .map(normalizeImei)
    .filter((s) => s.length > 0)
    .slice(0, 2)
    .map((norm, i) => ({
      workspace_id: input.workspace_id,
      id: newUuid(),
      device_id: deviceId,
      slot: (i + 1) as 1 | 2,
      imei_normalized: norm,
      imei_pta_status: input.pta_status,
      checked_at: null,
      checked_by: null,
      verification_method: null,
      evidence_media_id: null,
      revision: 1,
      created_at: now,
      updated_at: now,
      deleted_at: null,
    }))

  const device: Device = {
    workspace_id: input.workspace_id,
    id: deviceId,
    category_id: input.category_id,
    brand_code: input.brand_code,
    model: input.model.trim(),
    storage: input.storage ?? null,
    ram: input.ram ?? null,
    condition: input.condition ?? null,
    color: input.color ?? null,
    battery_health: null,
    sim_type: null,
    region_variant: null,
    pta_status: input.pta_status,
    carrier_lock_status: isApple ? (input.carrier_lock_status ?? 'unknown') : null,
    mdm_status: isApple ? (input.mdm_status ?? 'unknown') : null,
    activation_lock_status: null,
    inspection_notes: null,
    status: 'in_stock',
    revision: 1,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  }

  const purchase: Purchase = {
    workspace_id: input.workspace_id,
    id: newUuid(),
    device_id: deviceId,
    cost_paisa: input.cost_paisa,
    direct_cost_paisa: input.direct_cost_paisa,
    seller_contact_id: null,
    purchased_at: input.purchased_at ?? now,
    payment_status: 'unpaid',
    notes: null,
    revision: 1,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  }

  const payload: Record<string, unknown> = {
    category_id: device.category_id,
    brand_code: device.brand_code,
    model: device.model,
    storage: device.storage,
    ram: device.ram,
    condition: device.condition,
    color: device.color,
    pta_status: device.pta_status,
    carrier_lock_status: device.carrier_lock_status,
    mdm_status: device.mdm_status,
    imeis: imeis.map((im) => ({
      id: im.id,
      slot: im.slot,
      imei_normalized: im.imei_normalized,
      imei_pta_status: im.imei_pta_status,
    })),
    purchase: {
      id: purchase.id,
      cost_paisa: purchase.cost_paisa,
      direct_cost_paisa: purchase.direct_cost_paisa,
      purchased_at: purchase.purchased_at,
      payment_status: purchase.payment_status,
    },
  }

  return { device, imeis, purchase, payload }
}
